// App entry — v2
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import CookieConsent from "@/components/CookieConsent";
import BugReportButton from "@/components/BugReportButton";
import ScrollProgress from "@/components/ScrollProgress";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PageTransition } from "@/components/PageTransition";
import { PostHogPageTracker } from "@/components/PostHogPageTracker";
import PixelPageView from "@/components/PixelPageView";
import { AdminRoute } from "@/components/AdminRoute";
import { AuthRoute } from "@/components/AuthRoute";
import { lazy, Suspense, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
// Static imports — core marketplace pages live in the main bundle to avoid
// lazy-chunk TDZ crashes during route transitions. Bundle-size cost is
// accepted; correctness over micro-optimisation.
import RentAVan from "./pages/marketplace/RentAVan";
import VehicleDetail from "./pages/marketplace/VehicleDetail";
// BookVehicle is the renter's checkout entry point. Static-imported so the
// Reserve button on VehicleDetail can never land on a Suspense fallback
// while a lazy chunk is still resolving — the prior black-screen symptom.
import BookVehicle from "./pages/marketplace/BookVehicle";
// AuthCallback handles the email-verification redirect from Supabase. Must
// be a static import so the route resolves immediately when the user lands
// on it from the email link, with no Suspense/lazy step in between.
import AuthCallback from "./pages/AuthCallback";
// Lazy — heavy single-purpose flow (CSV import via papaparse, geocoding,
// per-row form state for N vans). Not on any landing-page path so we
// load it on demand to keep it out of the entry bundle.
const BulkVanOnboarding = lazy(() => import("./pages/fleet/BulkVanOnboarding.tsx"));
const Gateway = lazy(() => import("./pages/Gateway.tsx"));
const Index = lazy(() => import("./pages/Index.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const ServiceAgreement = lazy(() => import("./pages/ServiceAgreement.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const DMCA = lazy(() => import("./pages/DMCA.tsx"));
const Insurance = lazy(() => import("./pages/Insurance.tsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
// Programmatic SEO — city pages (lazy-loaded with data)
const CityPage = lazy(() => import("./components/CityPage.tsx"));
const CityListingsPage = lazy(() => import("./pages/marketplace/CityListingsPage.tsx"));

// Blog
const Blog = lazy(() => import("./pages/Blog.tsx"));
const CustomSprinterCostBlog = lazy(() => import("./pages/blog/CustomSprinterCostBlog.tsx"));
const SprintersOverSUVsBlog = lazy(() => import("./pages/blog/SprintersOverSUVsBlog.tsx"));
const HostEarningsBlog = lazy(() => import("./pages/blog/HostEarningsBlog.tsx"));
const BuildProcessBlog = lazy(() => import("./pages/blog/BuildProcessBlog.tsx"));
const Section179VanDeduction = lazy(() => import("./pages/blog/Section179VanDeduction.tsx"));
const RentalVanInvestment = lazy(() => import("./pages/blog/RentalVanInvestment.tsx"));
const SprinterVsSuburban = lazy(() => import("./pages/blog/SprinterVsSuburban.tsx"));
const VaurenStandard = lazy(() => import("./pages/blog/VaurenStandard.tsx"));
const HowToRentDallas = lazy(() => import("./pages/blog/HowToRentDallas.tsx"));
const WhatToExpectVaurenRental = lazy(() => import("./pages/blog/WhatToExpectVaurenRental.tsx"));
const MakeMoneySprintertDallas = lazy(() => import("./pages/blog/MakeMoneySprintertDallas.tsx"));
const LuxuryVanVsFlying = lazy(() => import("./pages/blog/LuxuryVanVsFlying.tsx"));
const HowToCommissionCustomVan = lazy(() => import("./pages/blog/HowToCommissionCustomVan.tsx"));
const TrueCostSprinterVanTexas = lazy(() => import("./pages/blog/TrueCostSprinterVanTexas.tsx"));

// Marketplace
const HowItWorks = lazy(() => import("./pages/marketplace/HowItWorks.tsx"));

// Host Acquisition Funnel
const HostHub = lazy(() => import("./pages/host/HostHub.tsx"));
const RevenueCalculator = lazy(() => import("./pages/host/RevenueCalculator.tsx"));
const HandsFreeHosting = lazy(() => import("./pages/host/HandsFreeHosting.tsx"));
const StartRentalBusiness = lazy(() => import("./pages/host/StartRentalBusiness.tsx"));
const OwnerStories = lazy(() => import("./pages/host/OwnerStories.tsx"));
const HostListYourVan = lazy(() => import("./pages/host/ListYourVan.tsx"));
const SignManagementAgreement = lazy(() => import("./pages/host/SignManagementAgreement.tsx"));
const ManageAFleet = lazy(() => import("./pages/marketplace/ManageAFleet.tsx"));
const FleetManagerApply = lazy(() => import("./pages/marketplace/FleetManagerApply.tsx"));
const FindFleetManager = lazy(() => import("./pages/marketplace/FindFleetManager.tsx"));
const FleetManagerProfile = lazy(() => import("./pages/marketplace/FleetManagerProfile.tsx"));
const FleetManagerSetup = lazy(() => import("./pages/marketplace/FleetManagerSetup.tsx"));
const FleetManagerDashboard = lazy(() => import("./pages/marketplace/FleetManagerDashboard.tsx"));
const MarketplaceAuth = lazy(() => import("./pages/marketplace/MarketplaceAuth.tsx"));
const VerifyEmail = lazy(() => import("./pages/marketplace/VerifyEmail.tsx"));
const ResetPassword = lazy(() => import("./pages/marketplace/ResetPassword.tsx"));
const MarketplaceDashboard = lazy(() => import("./pages/marketplace/Dashboard.tsx"));
const RentalAgreement = lazy(() => import("./pages/marketplace/RentalAgreement.tsx"));
const BookingConfirmed = lazy(() => import("./pages/marketplace/BookingConfirmed.tsx"));
const HostAgreement = lazy(() => import("./pages/marketplace/HostAgreement.tsx"));
const TripDetail = lazy(() => import("./pages/marketplace/TripDetail.tsx"));
const TripCheckIn = lazy(() => import("./pages/marketplace/TripCheckIn.tsx"));
const TripCheckOut = lazy(() => import("./pages/marketplace/TripCheckOut.tsx"));
const MarketplaceMessages = lazy(() => import("./pages/marketplace/Messages.tsx"));
const CreateListing = lazy(() => import("./pages/marketplace/CreateListing.tsx"));
const HostOnboardingWizard = lazy(() => import("./pages/marketplace/HostOnboardingWizard.tsx"));
const LeaveReview = lazy(() => import("./pages/marketplace/LeaveReview.tsx"));
const TripComplete = lazy(() => import("./pages/marketplace/TripComplete.tsx"));
const StripeSetupGuide = lazy(() => import("./pages/marketplace/StripeSetupGuide.tsx"));

// Marketplace City Pages
const DallasVanRentals = lazy(() => import("./pages/marketplace/cities/DallasVanRentals.tsx"));
const HoustonVanRentals = lazy(() => import("./pages/marketplace/cities/HoustonVanRentals.tsx"));
const AustinVanRentals = lazy(() => import("./pages/marketplace/cities/AustinVanRentals.tsx"));
const SanAntonioVanRentals = lazy(() => import("./pages/marketplace/cities/SanAntonioVanRentals.tsx"));
const HelpCenter = lazy(() => import("./pages/marketplace/HelpCenter.tsx"));
const BuyAVanLanding = lazy(() => import("./pages/buy-a-van/BuyAVanLanding.tsx"));
const CommissioningStudio = lazy(() => import("./pages/buy-a-van/CommissioningStudio.tsx"));
const BuildSummary = lazy(() => import("./pages/buy-a-van/BuildSummary.tsx"));
const CommercialJourney = lazy(() => import("./pages/buy-a-van/CommercialJourney.tsx"));
const DepositPage = lazy(() => import("./pages/buy-a-van/DepositPage.tsx"));
const DepositSuccess = lazy(() => import("./pages/buy-a-van/DepositSuccess.tsx"));
const FinancialProfile = lazy(() => import("./pages/buy-a-van/FinancialProfile.tsx"));
const BuyerDashboard = lazy(() => import("./pages/buy-a-van/BuyerDashboard.tsx"));
const BuilderDashboard = lazy(() => import("./pages/builder/BuilderDashboard.tsx"));
const BuilderSetup = lazy(() => import("./pages/builder/BuilderSetup.tsx"));
const BuilderPortal = lazy(() => import("./pages/builder/BuilderPortal.tsx"));
const BuildTracker = lazy(() => import("./pages/BuildTracker.tsx"));
const SupportChatbot = lazy(() => import("@/components/SupportChatbot"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));

// Landing pages (zero-navigation, paid traffic)
const BuyAVanLP = lazy(() => import("./pages/lp/BuyAVanLP.tsx"));
const RentalLP = lazy(() => import("./pages/lp/RentalLP.tsx"));
const HostLP = lazy(() => import("./pages/lp/HostLP.tsx"));
const WorldCupRenterLP = lazy(() => import("./pages/lp/WorldCupRenterLP.tsx"));
const BuildConfirmed = lazy(() => import("./pages/BuildConfirmed.tsx"));

// Tier pages
const VaurenI = lazy(() => import("./pages/tiers/VaurenI.tsx"));
const VaurenII = lazy(() => import("./pages/tiers/VaurenII.tsx"));
const VaurenIII = lazy(() => import("./pages/tiers/VaurenIII.tsx"));
const VaurenIV = lazy(() => import("./pages/tiers/VaurenIV.tsx"));

// Builder marketplace (Phase 2 placeholders)
const BuilderMarketplace = lazy(() => import("./pages/BuilderMarketplace.tsx"));
const BuilderApply = lazy(() => import("./pages/BuilderApply.tsx"));
const BuilderProfile = lazy(() => import("./pages/BuilderProfile.tsx"));
const Inventory = lazy(() => import("./pages/Inventory.tsx"));
const DealerStatusUpdate = lazy(() => import("./pages/DealerStatusUpdate.tsx"));

// Owner onboarding (Phase 2 placeholders)
const OwnerInviteAccept = lazy(() => import("./pages/owner/OwnerInviteAccept.tsx"));
const OwnerOnboarding = lazy(() => import("./pages/owner/OwnerOnboarding.tsx"));
const OwnerDashboard = lazy(() => import("./pages/owner/OwnerDashboard.tsx"));
const OwnerSettings = lazy(() => import("./pages/owner/OwnerSettings.tsx"));

// Fleet bulk onboarding — static so the heavy bulk form chunk never hits a
// Suspense fallback (the previous lazy variant was the same shape as the
// VehicleDetail TDZ regression). Imported alongside the other static
// renter/host critical-path pages.

// Vauren University
const University = lazy(() => import("./pages/University.tsx"));
const UniversityCourses = lazy(() => import("./pages/UniversityCourses.tsx"));
const UniversityPath = lazy(() => import("./pages/UniversityPath.tsx"));
const UniversityLesson = lazy(() => import("./pages/UniversityLesson.tsx"));
const UniversityHandbook = lazy(() => import("./pages/UniversityHandbook.tsx"));
const UniversityCertificate = lazy(() => import("./pages/UniversityCertificate.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 min — avoid unnecessary refetches on navigation
      gcTime: 1000 * 60 * 10,     // 10 min garbage collection
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Vauren-branded loading screen — cream surface + serif wordmark.
// Replaces the prior generic spinner so every lazy-route gap reads as
// intentional brand chrome instead of a blank or spinner. Used as the
// Suspense fallback for the SEO wrapper (which itself wraps every
// lazy-loaded route element), so updating it once propagates to every
// route in the table below.
const VaurenLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      background: "#F5F0EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: 20,
      color: "rgba(44,36,32,0.35)",
      letterSpacing: "0.25em",
      textTransform: "uppercase",
    }}
  >
    Vauren
  </div>
);

const SEO = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<VaurenLoader />}>
    <PageTransition>{children}</PageTransition>
  </Suspense>
);

const LazyCityRoute = lazy(() =>
  import("./data/cities").then((mod) => ({
    default: () => {
      const { slug } = useParams<{ slug: string }>();
      const city = mod.getCityBySlug("/rentals/" + slug);
      if (!city) return <Navigate to="/404" replace />;
      return <CityPage city={city} />;
    },
  }))
);

const CityRoute = () => (
  <SEO>
    <LazyCityRoute />
  </SEO>
);

// Lazy-loaded full city directory page
const CityDirectoryPage = lazy(() => import("./pages/CityDirectory.tsx"));

const AnimatedRoutes = () => {
  const location = useLocation();

  // Capture UTM params on every route change
  useEffect(() => {
    if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem("vauren_utm")) {
      const params = new URLSearchParams(location.search);
      const utm: Record<string, string> = {};
      for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"]) {
        const val = params.get(key);
        if (val) utm[key] = val;
      }
      if (Object.keys(utm).length > 0) sessionStorage.setItem("vauren_utm", JSON.stringify(utm));
    }
  }, [location]);

  return (
    <Routes>
        <Route path="/" element={<SEO><Gateway /></SEO>} />
        <Route path="/sprinter-vans" element={<SEO><Index /></SEO>} />
        <Route path="/pricing" element={<SEO><Pricing /></SEO>} />
        <Route path="/about" element={<SEO><About /></SEO>} />
        <Route path="/technology" element={<Navigate to="/about" replace />} />
        <Route path="/contact" element={<SEO><Contact /></SEO>} />
        <Route path="/service-agreement" element={<SEO><ServiceAgreement /></SEO>} />
        <Route path="/privacy" element={<SEO><Privacy /></SEO>} />
        <Route path="/terms" element={<SEO><Terms /></SEO>} />
        <Route path="/dmca" element={<SEO><DMCA /></SEO>} />
        <Route path="/insurance" element={<SEO><Insurance /></SEO>} />
        <Route path="/admin-login" element={<SEO><AdminLogin /></SEO>} />
        <Route path="/admin" element={<SEO><AdminRoute><Admin /></AdminRoute></SEO>} />

        {/* Landing pages — zero navigation, paid traffic */}
        <Route path="/lp/buy-a-van" element={<SEO><BuyAVanLP /></SEO>} />
        <Route path="/lp/rental" element={<SEO><RentalLP /></SEO>} />
        <Route path="/lp/host" element={<SEO><HostLP /></SEO>} />
        {/* World Cup paid-traffic LP — no nav, no footer (page does
            not import PageLayout). Single conversion path → /rent-a-van. */}
        <Route path="/lp/worldcup" element={<SEO><WorldCupRenterLP /></SEO>} />
        <Route path="/build-confirmed" element={<SEO><BuildConfirmed /></SEO>} />

        {/* Tier pages */}
        <Route path="/tiers/vauren-i" element={<SEO><VaurenI /></SEO>} />
        <Route path="/tiers/vauren-ii" element={<SEO><VaurenII /></SEO>} />
        <Route path="/tiers/vauren-iii" element={<SEO><VaurenIII /></SEO>} />
        <Route path="/tiers/vauren-iv" element={<SEO><VaurenIV /></SEO>} />

        {/* Blog */}
        <Route path="/blog" element={<SEO><Blog /></SEO>} />
        <Route path="/blog/how-much-custom-sprinter-van-cost" element={<SEO><CustomSprinterCostBlog /></SEO>} />
        <Route path="/blog/why-executives-choose-sprinter-vans-over-suvs" element={<SEO><SprintersOverSUVsBlog /></SEO>} />
        <Route path="/blog/earn-money-hosting-luxury-van" element={<SEO><HostEarningsBlog /></SEO>} />
        <Route path="/blog/custom-van-build-commission-to-delivery" element={<SEO><BuildProcessBlog /></SEO>} />
        <Route path="/blog/section-179-van-tax-deduction-2026" element={<SEO><Section179VanDeduction /></SEO>} />
        <Route path="/blog/luxury-van-rental-investment-roi" element={<SEO><RentalVanInvestment /></SEO>} />
        <Route path="/blog/sprinter-van-vs-suburban-yukon-xl" element={<SEO><SprinterVsSuburban /></SEO>} />
        <Route path="/blog/what-is-the-vauren-vehicle-standard" element={<SEO><VaurenStandard /></SEO>} />
        <Route path="/blog/how-to-rent-luxury-sprinter-van-dallas" element={<SEO><HowToRentDallas /></SEO>} />
        <Route path="/blog/what-to-expect-vauren-rental" element={<SEO><WhatToExpectVaurenRental /></SEO>} />
        <Route path="/blog/make-money-sprinter-van-dallas" element={<SEO><MakeMoneySprintertDallas /></SEO>} />
        <Route path="/blog/luxury-van-vs-flying-family-travel" element={<SEO><LuxuryVanVsFlying /></SEO>} />
        <Route path="/blog/how-to-commission-custom-sprinter-van-build" element={<SEO><HowToCommissionCustomVan /></SEO>} />
        <Route path="/blog/true-cost-owning-sprinter-van-texas" element={<SEO><TrueCostSprinterVanTexas /></SEO>} />
        {/* Marketplace */}
        <Route path="/rent-a-van" element={<SEO><RentAVan /></SEO>} />
        <Route path="/marketplace/rent" element={<SEO><RentAVan /></SEO>} />
        {/* Host funnel consolidation (2026-09-10): these three pages were
            retired — thin duplicates of HostHub/host-ListYourVan with no
            (list-your-van, make-money-with-your-sprinter) or zero
            (host-a-van) inbound internal links. Their unique content was
            merged into /host and /host/list-your-van before removal. */}
        <Route path="/list-your-van" element={<Navigate to="/host/list-your-van" replace />} />
        <Route path="/host-a-van" element={<Navigate to="/host" replace />} />
        <Route path="/host/make-money-with-your-sprinter" element={<Navigate to="/host" replace />} />
        <Route path="/host" element={<SEO><HostHub /></SEO>} />
        {/* Token-authenticated public route — the agreement_token in
            the URL is the only authentication. No auth guard. */}
        <Route path="/agreement/sign" element={<SEO><SignManagementAgreement /></SEO>} />
        <Route path="/host/revenue-calculator" element={<SEO><RevenueCalculator /></SEO>} />
        <Route path="/host/hands-free-hosting" element={<SEO><HandsFreeHosting /></SEO>} />
        <Route path="/host/start-a-sprinter-rental-business" element={<SEO><StartRentalBusiness /></SEO>} />
        <Route path="/host/owner-stories" element={<SEO><OwnerStories /></SEO>} />
        <Route path="/host/list-your-van" element={<SEO><HostListYourVan /></SEO>} />
        <Route path="/manage-a-fleet" element={<SEO><ManageAFleet /></SEO>} />
        <Route path="/fleet-manager-apply" element={<SEO><FleetManagerApply /></SEO>} />
        <Route path="/find-fleet-manager" element={<SEO><FindFleetManager /></SEO>} />
        <Route path="/fleet-manager/setup" element={<AuthRoute><SEO><FleetManagerSetup /></SEO></AuthRoute>} />
        <Route path="/fleet-manager/dashboard" element={<AuthRoute><SEO><FleetManagerDashboard /></SEO></AuthRoute>} />
        <Route path="/fleet-manager/:id" element={<SEO><FleetManagerProfile /></SEO>} />
        <Route path="/how-it-works" element={<SEO><HowItWorks /></SEO>} />
        <Route path="/marketplace/auth" element={<SEO><MarketplaceAuth /></SEO>} />
        <Route path="/marketplace/verify-email" element={<SEO><VerifyEmail /></SEO>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/marketplace/reset-password" element={<SEO><ResetPassword /></SEO>} />
        <Route path="/marketplace/dashboard" element={<AuthRoute><SEO><MarketplaceDashboard /></SEO></AuthRoute>} />
        <Route path="/marketplace/vehicle/:id" element={<VehicleDetail />} />
        <Route path="/marketplace/book/:id" element={<AuthRoute><BookVehicle /></AuthRoute>} />
        <Route path="/marketplace/rental-agreement" element={<AuthRoute><SEO><RentalAgreement /></SEO></AuthRoute>} />
        <Route path="/marketplace/booking-confirmed/:id" element={<AuthRoute><SEO><BookingConfirmed /></SEO></AuthRoute>} />
        <Route path="/marketplace/host-agreement" element={<AuthRoute><SEO><HostAgreement /></SEO></AuthRoute>} />
        <Route path="/marketplace/trip/:id" element={<AuthRoute><SEO><TripDetail /></SEO></AuthRoute>} />
        <Route path="/marketplace/trip/:id/check-in" element={<AuthRoute><SEO><TripCheckIn /></SEO></AuthRoute>} />
        <Route path="/marketplace/trip/:id/check-out" element={<AuthRoute><SEO><TripCheckOut /></SEO></AuthRoute>} />
        <Route path="/marketplace/messages" element={<AuthRoute><SEO><MarketplaceMessages /></SEO></AuthRoute>} />
        <Route path="/marketplace/create-listing" element={<AuthRoute><SEO><CreateListing /></SEO></AuthRoute>} />
        <Route path="/host/onboarding" element={<AuthRoute><SEO><HostOnboardingWizard /></SEO></AuthRoute>} />
        <Route path="/marketplace/review/:tripId" element={<AuthRoute><SEO><LeaveReview /></SEO></AuthRoute>} />
        <Route path="/marketplace/trip-complete/:bookingId" element={<AuthRoute><SEO><TripComplete /></SEO></AuthRoute>} />
        <Route path="/marketplace/stripe-setup-guide" element={<AuthRoute><SEO><StripeSetupGuide /></SEO></AuthRoute>} />

        {/* Marketplace City Pages */}
        <Route path="/rent-a-van/dallas" element={<SEO><DallasVanRentals /></SEO>} />
        <Route path="/rent-a-van/houston" element={<SEO><HoustonVanRentals /></SEO>} />
        <Route path="/rent-a-van/austin" element={<SEO><AustinVanRentals /></SEO>} />
        <Route path="/rent-a-van/san-antonio" element={<SEO><SanAntonioVanRentals /></SEO>} />
        <Route path="/marketplace/help" element={<SEO><HelpCenter /></SEO>} />
        <Route path="/buy-a-van" element={<SEO><BuyAVanLanding /></SEO>} />
        <Route path="/buy-a-van/personal" element={<Navigate to="/build" replace />} />
        <Route path="/buy-a-van/commercial" element={<SEO><CommercialJourney /></SEO>} />
        <Route path="/buy-a-van/deposit" element={<SEO><DepositPage /></SEO>} />
        <Route path="/buy-a-van/deposit-success" element={<SEO><DepositSuccess /></SEO>} />
        <Route path="/buy-a-van/financial-profile" element={<SEO><FinancialProfile /></SEO>} />
        <Route path="/buy-a-van/dashboard" element={<AuthRoute><SEO><BuyerDashboard /></SEO></AuthRoute>} />
        <Route path="/builder" element={<AuthRoute><SEO><BuilderDashboard /></SEO></AuthRoute>} />
        <Route path="/builder/setup" element={<AuthRoute><SEO><BuilderSetup /></SEO></AuthRoute>} />
        <Route path="/builder-portal" element={<AuthRoute><SEO><BuilderPortal /></SEO></AuthRoute>} />
        <Route path="/track" element={<SEO><BuildTracker /></SEO>} />

        {/* Programmatic SEO — City Pages */}
        <Route path="/rentals" element={<SEO><CityDirectoryPage /></SEO>} />
        {/* Dynamic city listings driven by vehicles.service_locations.
            Matches BEFORE the legacy /:slug catch-all so existing
            programmatic city pages still resolve. */}
        <Route
          path="/rentals/:state/:city"
          element={
            <SEO>
              <Suspense fallback={<VaurenLoader />}>
                <CityListingsPage />
              </Suspense>
            </SEO>
          }
        />
        <Route path="/rentals/:slug" element={<CityRoute />} />

        {/* Builder marketplace (Phase 2 placeholders) */}
        <Route path="/builders" element={<SEO><BuilderMarketplace /></SEO>} />
        <Route path="/builders/apply" element={<SEO><BuilderApply /></SEO>} />
        <Route path="/builders/:slug" element={<SEO><BuilderProfile /></SEO>} />
        <Route path="/inventory" element={<SEO><Inventory /></SEO>} />
        <Route path="/dealer/update" element={<SEO><DealerStatusUpdate /></SEO>} />

        {/* Buy-a-van alias */}
        <Route path="/build" element={<SEO><CommissioningStudio /></SEO>} />
        <Route path="/build/summary/:token" element={<SEO><BuildSummary /></SEO>} />

        {/* Owner onboarding (Phase 2 placeholders) */}
        <Route path="/owner/invite/:token" element={<SEO><OwnerInviteAccept /></SEO>} />
        <Route path="/owner/setup" element={<AuthRoute><SEO><OwnerOnboarding /></SEO></AuthRoute>} />
        <Route path="/owner/dashboard" element={<AuthRoute><SEO><OwnerDashboard /></SEO></AuthRoute>} />
        <Route path="/owner/settings" element={<AuthRoute><SEO><OwnerSettings /></SEO></AuthRoute>} />

        {/* Fleet bulk onboarding (Phase 2 placeholder) */}
        <Route path="/fleet/bulk-onboard" element={<AuthRoute><SEO><BulkVanOnboarding /></SEO></AuthRoute>} />

        {/* Vauren University */}
        {/* University ships large nested lazy chunks (GSAP scroll
            engine, ParticleField, lesson content). The outer SEO
            Suspense already covers the initial /university lazy
            import; this inner Suspense catches any nested
            suspensions inside the page so the user sees the cream
            wordmark instead of the prior page snapping back to a
            stale frame while a child chunk resolves. */}
        <Route
          path="/university"
          element={
            <SEO>
              <Suspense fallback={<VaurenLoader />}>
                <University />
              </Suspense>
            </SEO>
          }
        />
        <Route path="/university/courses" element={<SEO><UniversityCourses /></SEO>} />
        <Route path="/university/path/:slug" element={<SEO><UniversityPath /></SEO>} />
        <Route path="/university/lesson/:slug" element={<SEO><UniversityLesson /></SEO>} />
        <Route path="/university/handbook" element={<SEO><UniversityHandbook /></SEO>} />
        <Route path="/university/certificate/:id" element={<SEO><UniversityCertificate /></SEO>} />

        <Route path="*" element={<SEO><NotFound /></SEO>} />
      </Routes>
  );
};

const App = () => {
  // Global SIGNED_OUT cleanup. Runs at the App root so every dashboard,
  // onboarding draft, and browsing-history key gets cleared regardless
  // of which logout handler the user clicked. `window.location.href` is
  // used (not useNavigate) because App is rendered OUTSIDE BrowserRouter
  // — useNavigate would throw at this scope. The full reload is also a
  // stronger boundary: React Query cache, in-memory token captures, and
  // any component state are dropped along with the page.
  //
  // Cookie consent keys (vauren_cookie_consent, vauren_cookie_preferences)
  // are intentionally NOT cleared — GDPR consent persists across user
  // accounts on the same browser.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Tag Sentry events with the signed-in user id so we can trace a
      // crash back to the affected account without storing PII. Email,
      // username, and IP are stripped in main.tsx's beforeSend.
      if (event === "SIGNED_IN" && session?.user) {
        Sentry.setUser({ id: session.user.id });
        return;
      }

      if (event !== "SIGNED_OUT") return;

      Sentry.setUser(null);

      const keysToRemove = [
        "vauren_booking_intent",
        "vauren_post_auth_redirect",
        "vauren_draft_config",
        "vauren_host_onboarding_draft_v1",
        "vauren_bulk_onboard_draft_v2",
        "vauren-handbook",
        "vauren_recently_viewed",
      ];
      for (const key of keysToRemove) localStorage.removeItem(key);

      sessionStorage.clear();
      window.location.href = "/";
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    // SENTRY_TEST: To verify Sentry is active after
    // deploying with VITE_SENTRY_DSN set, open the
    // browser console on vauren.com and run:
    // Sentry.captureMessage('Sentry test from Vauren')
    // Then check sentry.io/organizations/dropup-fx/issues
    // Sentry.ErrorBoundary catches uncaught React rendering errors
    <Sentry.ErrorBoundary
      fallback={
        <div style={{
          padding: 40,
          fontFamily: 'DM Sans, sans-serif',
          color: '#2C2420',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: 18, marginBottom: 8 }}>
            Something went wrong.
          </p>
          <p style={{ fontSize: 14, color: '#6B6B60' }}>
            Our team has been notified. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16,
              padding: '10px 24px',
              background: '#B8976A',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 14
            }}
          >
            Refresh
          </button>
        </div>
      }
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* Marketplace-scoped luxury woven background lives in
              index.css as a `.marketplace-page::before` pseudo-element
              with a hardcoded SVG data URI. PageLayout adds that class
              only on CREAM_PATHS, so the pattern never paints on the
              homepage, LPs, or info pages. No React component needed. */}
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PostHogPageTracker />
            <PixelPageView />
            <ScrollProgress />
            <AnimatedRoutes />
            <Suspense fallback={null}>
              <SupportChatbot />
            </Suspense>
            <CookieConsent />
            <BugReportButton />
            {/* Mobile-only global rules. Injected from App so we don't
                have to touch index.css. iOS Safari zooms the viewport on
                any input/textarea/select with font-size < 16px when the
                user taps it — this rule keeps the existing visual style
                (font-size still computed from the component's class on
                desktop) but forces 16px on the form elements at <768px
                so we don't lose context every time someone fills in
                a field on a phone. */}
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  @media (max-width: 767px) {
                    input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
                    textarea,
                    select {
                      font-size: 16px !important;
                    }
                  }
                `,
              }}
            />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
